import {inject} from '@angular/core';
import {HttpErrorResponse, HttpInterceptorFn} from '@angular/common/http';
import {BehaviorSubject, Observable, throwError} from 'rxjs';
import {catchError, filter, switchMap, take} from 'rxjs/operators';
import {TokenService} from '../../features/auth/services/token.service';
import {AuthService} from '../../features/auth/services/auth.service';
import { SnackbarService } from '@/app/shared/services/snackbar.service';
import { Router } from '@angular/router';

let isRefreshing = false;
let refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const snackbarService = inject(SnackbarService);
  const tokenService = inject(TokenService);
  const router = inject(Router);

  const modifiedRequest = addTokenHeader(req, tokenService);

  return next(modifiedRequest).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && !req.url.includes('/account/login')) {
        if (req.url.includes('/account/refresh-token')) {
          logout(tokenService, router, snackbarService);
          return throwError(() => new Error('Refresh token expired or invalid'));
        }
        return handle401Error(req, next, tokenService, router, snackbarService);
      }

      return throwError(() => error);
    })
  );
};

function addTokenHeader(request: any, tokenService: TokenService) {
  const token = tokenService.getAccessToken();

  if (token && !request.url.includes('/account/login')) {
    return request.clone({
      headers: request.headers.set('Authorization', `Bearer ${token}`)
    });
  }

  return request;
}

function handle401Error(request: any, next: any, tokenService: TokenService, router: Router, snackbarService: SnackbarService): Observable<any> {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    const refreshToken = tokenService.getRefreshToken();

    if (refreshToken) {
      return tokenService.refreshToken().pipe(
        switchMap((response: any) => {
          isRefreshing = false;

          if (response.accessToken) {
            tokenService.setTokens(response.accessToken, response.refreshToken || refreshToken);
            refreshTokenSubject.next(response.accessToken);

            return next(addTokenHeader(request, tokenService));
          }

          // Si no hay token en la respuesta, limpiar y redirigir
          logout(tokenService, router, snackbarService);
          return throwError(() => new Error('Token refresh failed'));
        }),
        catchError((error) => {
          isRefreshing = false;
          logout(tokenService, router, snackbarService);
          return throwError(() => error);
        })
      );
    }
  }

  return refreshTokenSubject.pipe(
    filter(token => token !== null),
    take(1),
    switchMap(() => next(addTokenHeader(request, tokenService)))
  );
}

function logout(tokenService: TokenService, router: Router, snackbarService: SnackbarService): void {
  tokenService.clearTokens();
  snackbarService.showError('Su sesión ha expirado. Por favor, vuelva a iniciar sesión.');

  // Wait for snackbar to be dismissed before navigating
  setTimeout(() => {
    router.navigate(['/iniciar']);
  }, 1000);
}
